import {
  ComponentMirror,
  ComponentRef,
  Directive,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  reflectComponentType,
  SimpleChange,
  SimpleChanges,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type UserOutputs = Record<string, (event: any) => void>;
type UserInputs = Record<string, any>;
type ComponentInputs = ComponentMirror<any>['inputs'];
type ComponentOutputs = ComponentMirror<any>['outputs'];

@Directive({
  selector: '[dynamic-component]',
})
export class DynamicComponentDirective implements OnDestroy, OnChanges {
  @Input('dynamic-component') public component!: Type<any>;
  @Input() public outputs?: UserOutputs = {};
  @Input() public inputs?: UserInputs = {};
  @Input() public injector?: Injector;
  @Input() public stepIndex?: number;
  @Output() public componentCreated: EventEmitter<ComponentRef<any>> = new EventEmitter<ComponentRef<any>>();

  private destroy$: Subject<void> = new Subject<void>();
  private componentMirror?: ComponentMirror<any>;
  private componentRef?: ComponentRef<any>;

  constructor(private viewContainerRef: ViewContainerRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    assertNotNullOrUndefined(this.component);

    let componentChanges: Record<string, SimpleChange>;
    const shouldCreateNewComponent: boolean =
      changes.component?.previousValue !== changes.component?.currentValue ||
      changes.injector?.previousValue !== changes.injector?.currentValue ||
      changes.stepIndex?.previousValue !== changes.stepIndex?.currentValue;

    if (shouldCreateNewComponent) {
      this.destroyComponent();
      this.createComponent();
      componentChanges = this.makeComponentChanges(changes.inputs, true);
    }
    componentChanges ??= this.makeComponentChanges(changes.inputs, false);

    assertNotNullOrUndefined(this.componentRef);
    this.componentMirror = reflectComponentType(this.component);

    this.validateOutputs(this.componentMirror.outputs, this.outputs ?? {}, this.componentRef.instance);
    this.validateInputs(this.componentMirror.inputs, this.inputs ?? {});
    if (changes.inputs) {
      this.bindInputs(this.componentMirror.inputs, this.inputs ?? {}, this.componentRef.instance);
    }
    if (changes.outputs) {
      this.destroy$.next(); // to remove old subscription
      this.bindOutputs(this.componentMirror.outputs, this.outputs ?? {}, this.componentRef.instance);
    }
    if ((this.componentRef.instance as OnChanges).ngOnChanges) {
      this.componentRef.instance.ngOnChanges(componentChanges);
    }
  }

  public ngOnDestroy(): void {
    this.destroyComponent();
    this.destroy$.next();
  }

  private makeComponentChanges(inputsChange: SimpleChange, firstChange: boolean): Record<string, SimpleChange> {
    const previuosInputs = inputsChange?.previousValue ?? {};
    const currentInputs = inputsChange?.currentValue ?? {};
    return Object.keys(currentInputs).reduce((acc: Record<string, SimpleChange>, inputName: string) => {
      const currentInputValue = currentInputs[inputName];
      const previuosInputValue = previuosInputs[inputName];
      if (currentInputValue !== previuosInputValue) {
        acc[inputName] = new SimpleChange(firstChange ? undefined : previuosInputValue, currentInputValue, firstChange);
      }
      return acc;
    }, {} as Record<string, SimpleChange>);
  }

  private createComponent(): void {
    this.componentRef = this.viewContainerRef.createComponent<any>(this.component, { index: 0, injector: this.injector });
    this.componentCreated.emit(this.componentRef);
  }

  private bindOutputs(componentOutputs: ComponentInputs, userOutputs: UserInputs, componentInstance: any): void {
    componentOutputs.forEach((output) => {
      (componentInstance[output.propName] as EventEmitter<any>).pipe(takeUntil(this.destroy$)).subscribe((event: void) => {
        const handler = userOutputs[output.templateName];
        if (handler) {
          // in case the output has not been provided at all
          handler(event);
        }
      });
    });
  }

  private bindInputs(componentInputs: ComponentInputs, userInputs: UserInputs, componentInstance: any): void {
    componentInputs.forEach((input) => {
      const inputValue = userInputs[input.templateName];
      componentInstance[input.propName] = inputValue;
    });
  }

  private validateOutputs(componentOutputs: ComponentOutputs, userOutputs: UserOutputs, componentInstance: any): void {
    componentOutputs.forEach((output) => {
      if (!(componentInstance[output.propName] instanceof EventEmitter)) {
        throw new Error(`Output ${output.propName} must be a typeof EventEmitter`);
      }
    });

    const outputsKeys = Object.keys(userOutputs);
    outputsKeys.forEach((key) => {
      const componentHaveThatOutput = componentOutputs.some((output) => output.templateName === key);
      if (!componentHaveThatOutput) {
        throw new Error(`Output ${key} is not ${this.component.name} output.`);
      }
      if (!(userOutputs[key] instanceof Function)) {
        throw new Error(`Output ${key} must be a function`);
      }
    });
  }

  private validateInputs(componentInputs: ComponentInputs, userInputs: UserInputs): void {
    const userInputsKeys = Object.keys(userInputs);
    userInputsKeys.forEach((userInputKey) => {
      const componentHaveThatInput = componentInputs.some((componentInput) => componentInput.templateName === userInputKey);
      if (!componentHaveThatInput) {
        throw new Error(`Input ${userInputKey} is not ${this.component.name} input.`);
      }
    });
  }

  private destroyComponent(): void {
    this.componentRef?.destroy();
    this.viewContainerRef.clear();
  }
}

function assertNotNullOrUndefined<T>(value: T): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error(`cannot be undefined or null`);
  }
}
